import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Button, inputStyle, Notice, Segmented } from "../components/ui.js";
import {
  dropPoolOwnRules,
  fetchAuctionRules,
  givePoolOwnRules,
  LEFTOVERS_LABELS,
  FORCED_PRICE_LABELS,
  NEXT_UP_LABELS,
  saveAuctionRules,
  setSquadFill,
  slabErrors,
  SQUAD_FILL_LABELS,
  slabsLine,
  UNSOLD_LABELS,
} from "../lib/auction-rules.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { AUCTION_CURRENCIES, type AuctionCurrency } from "../lib/currency.js";
import { captainsIn, fetchFranchises } from "../lib/franchises.js";
import { fetchTournaments, setAuctionCurrency } from "../lib/tournaments.js";
import type {
  AuctionRules,
  AuctionRulesSetup,
  SquadFill,
  Tournament,
  TournamentEvent,
} from "../lib/types.js";
import { LotsLinkRow } from "./TournamentLots.js";

/** A number field in a row: the hairline frame the lots page uses, 44px for a thumb. */
const rowControl = {
  ...inputStyle,
  border: "1px solid var(--color-line-muted)",
  minHeight: 44,
  fontSize: 14,
} as const;

const rulesKey = (tournamentId: string) => ["auction-rules", tournamentId] as const;

/**
 * Whether the viewer is shown the auction rules: organisers, and anybody who captains a squad in
 * one of the tournament's pools. Nobody else is, though the database would still hand them the
 * rules if asked: this decides what the app shows, not what is secret.
 */
function useSeesRules(tournamentId: string): { pending: boolean; sees: boolean } {
  const { member } = useAuth();
  const organiser = hasRole(member, "organiser");
  const setup = useQuery({
    queryKey: ["franchises", tournamentId],
    queryFn: () => fetchFranchises(tournamentId),
    enabled: !organiser && tournamentId !== "",
  });
  if (organiser) return { pending: false, sees: true };
  if (setup.isPending) return { pending: true, sees: false };
  return { pending: false, sees: setup.data ? captainsIn(setup.data, member?.id) : false };
}

/**
 * The rules an auction tournament's auction runs by: bid increments in slabs, what a captain
 * costs, the purse reserve, and what happens to an uneven lot, unsold players and who comes up
 * next. The tournament's rules first, then each pool, which follows them or has its own.
 *
 * Organisers and captains read this page, so captains know the increments before they bid;
 * anybody else is told there are no rules to show. Organisers change it until the auction
 * starts. The database refuses every write that the page would not offer.
 */
export function TournamentRules() {
  const { tournamentId = "" } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();
  const tournaments = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });
  const rules = useQuery({
    queryKey: rulesKey(tournamentId),
    queryFn: () => fetchAuctionRules(tournamentId),
  });
  const tournament = tournaments.data?.find((candidate) => candidate.id === tournamentId);
  const viewer = useSeesRules(tournamentId);

  if (tournaments.isPending || rules.isPending || viewer.pending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (tournaments.error || rules.error || !tournament || !rules.data) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {tournaments.error || rules.error
            ? "Couldn't load the rules. Try again."
            : "This tournament has no auction rules."}
        </Notice>
      </main>
    );
  }

  if (!viewer.sees) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to={`/tournaments/${tournament.id}`} className="micro mb-4 inline-block underline">
          ← {tournament.name}
        </Link>
        <Notice>No rules to show.</Notice>
      </main>
    );
  }

  const organiser = hasRole(member, "organiser");
  const editable = organiser && tournament.auctionStartedAt === null;

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link
        to={organiser ? `/tournaments/${tournament.id}/manage` : `/tournaments/${tournament.id}`}
        className="micro mb-4 inline-block underline"
      >
        ← {tournament.name}
      </Link>
      <PageHeader
        eyebrow={`${organiser ? "Organise" : "Auction"} · ${tournament.name}`}
        title="Auction rules"
      />
      {tournament.auctionStartedAt !== null ? (
        <Notice>The auction has started, so these rules are fixed.</Notice>
      ) : editable ? (
        <p className="mb-6 text-sm text-ink-muted">
          Every pool runs on these rules unless you give it its own. Captains can read them, so they
          know the increments before they bid.
        </p>
      ) : null}

      <CurrencySection tournament={tournament} editable={editable} />

      <section className="mb-8">
        <h2 className="micro mb-3">The tournament</h2>
        {editable ? (
          <RulesEditor
            key={JSON.stringify(rules.data.tournament)}
            tournament={tournament}
            eventId={null}
            rules={rules.data.tournament}
          />
        ) : (
          <RulesSummary rules={rules.data.tournament} currency={tournament.auctionCurrency} />
        )}
      </section>

      {tournament.events.length > 0 ? (
        <section className="border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
          <h2 className="micro mb-1">Pools</h2>
          <ul>
            {tournament.events.map((event) => (
              <PoolRules
                key={event.id}
                tournament={tournament}
                event={event}
                setup={rules.data!}
                editable={editable}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </main>
  );
}

/**
 * The label beside every price in this auction. A label only: prices stay whole numbers and
 * nothing converts. Chosen with the rules and fixed with them when the auction starts.
 */
function CurrencySection({ tournament, editable }: { tournament: Tournament; editable: boolean }) {
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (next: AuctionCurrency) => setAuctionCurrency(tournament.id, next),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["tournaments"] }),
  });
  const current = AUCTION_CURRENCIES.find((c) => c.code === tournament.auctionCurrency);

  return (
    <section className="mb-8">
      <h2 className="micro mb-3">Currency</h2>
      {editable ? (
        <>
          <p className="mb-3 text-sm text-ink-muted">
            Shown beside every price, purse and step. Only the label changes, never the numbers.
          </p>
          <Segmented
            label="Currency"
            value={tournament.auctionCurrency}
            onChange={(next) => save.mutate(next)}
            options={AUCTION_CURRENCIES.map((c) => ({ value: c.code, label: c.code }))}
          />
          {save.error ? (
            <p className="mt-2 text-sm" style={{ color: "var(--color-attention)" }}>
              {save.error.message}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-sm">
          {tournament.auctionCurrency}
          {current ? ` · ${current.name}` : ""}
        </p>
      )}
    </section>
  );
}

/** One pool: whether it follows the tournament, and its own rules when it has them. */
function PoolRules({
  tournament,
  event,
  setup,
  editable,
}: {
  tournament: Tournament;
  event: TournamentEvent;
  setup: AuctionRulesSetup;
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const own = setup.pools.get(event.id) ?? null;

  const fill = useMutation({
    mutationFn: (next: SquadFill) => setSquadFill(event.id, next),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["tournaments"] }),
  });

  const change = useMutation({
    mutationFn: () => (own ? dropPoolOwnRules(event.id) : givePoolOwnRules(event.id)),
    onSuccess: () => {
      setEditing(!own);
      void queryClient.invalidateQueries({ queryKey: rulesKey(tournament.id) });
    },
  });

  return (
    <li className="border-b py-3" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-base font-bold">{event.name}</span>
          <span className="font-mono text-[11px] text-ink-muted">
            {event.squadFill === "assigned"
              ? "Players placed by the organiser"
              : own
                ? `Own rules · ${slabsLine(own.slabs, tournament.auctionCurrency)}`
                : "Follows the tournament"}
          </span>
        </span>
        {editable && event.squadFill === "auction" ? (
          <span className="flex gap-2">
            {own ? (
              <Button
                tone="quiet"
                onClick={() => setEditing((open) => !open)}
                aria-expanded={editing}
                aria-label={`${editing ? "Close" : "Edit"} the rules of ${event.name}`}
              >
                {editing ? "Close" : "Edit"}
              </Button>
            ) : null}
            <Button
              tone="line"
              disabled={change.isPending}
              onClick={() => change.mutate()}
              aria-label={
                own
                  ? `Put ${event.name} back on the tournament's rules`
                  : `Give ${event.name} its own rules`
              }
            >
              {own ? "Follow the tournament" : "Use own rules"}
            </Button>
          </span>
        ) : null}
      </div>
      {editable ? (
        <Segmented
          label={`How ${event.name} is filled`}
          value={event.squadFill}
          onChange={(next) => fill.mutate(next)}
          options={[
            { value: "auction", label: SQUAD_FILL_LABELS.auction },
            { value: "assigned", label: SQUAD_FILL_LABELS.assigned },
          ]}
          className="mt-3 max-w-sm"
        />
      ) : null}
      {fill.error ? <Notice tone="attention">{fill.error.message}</Notice> : null}
      {change.error ? <Notice tone="attention">{change.error.message}</Notice> : null}
      {own && editing && editable && event.squadFill === "auction" ? (
        <div className="mt-4 border-l-2 pl-4" style={{ borderColor: "var(--color-line)" }}>
          <RulesEditor
            key={JSON.stringify(own)}
            tournament={tournament}
            eventId={event.id}
            rules={own}
          />
        </div>
      ) : own && !editable && event.squadFill === "auction" ? (
        <div className="mt-3">
          <RulesSummary rules={own} currency={tournament.auctionCurrency} />
        </div>
      ) : null}
    </li>
  );
}

/** A slab as typed: text, so a field can be empty on the way to a number. */
interface SlabDraft {
  from: string;
  step: string;
}

const toNumber = (text: string) => (text.trim() === "" ? Number.NaN : Number(text));

/**
 * One set of rules as a form. Mistakes in the slabs show beside the slab and keep Save off; the
 * database checks the same things again and has the last word.
 */
function RulesEditor({
  tournament,
  eventId,
  rules,
}: {
  tournament: Tournament;
  eventId: string | null;
  rules: AuctionRules;
}) {
  const queryClient = useQueryClient();
  const id = useId();
  const [slabs, setSlabs] = useState<SlabDraft[]>(
    rules.slabs.map((s) => ({ from: String(s.from), step: String(s.step) })),
  );
  const [purseReserve, setPurseReserve] = useState(rules.purseReserve);
  const [leftovers, setLeftovers] = useState(rules.leftovers);
  const [unsold, setUnsold] = useState(rules.unsold);
  const [nextUp, setNextUp] = useState(rules.nextUp);
  const [forcedPrice, setForcedPrice] = useState(rules.forcedPrice);
  const [board, setBoard] = useState(rules.board);

  const parsed = slabs.map((s) => ({ from: toNumber(s.from), step: toNumber(s.step) }));
  const errors = slabErrors(parsed);

  const draft: AuctionRules = {
    purseReserve,
    leftovers,
    unsold,
    nextUp,
    forcedPrice,
    board,
    slabs: parsed,
  };
  const changed = JSON.stringify(draft) !== JSON.stringify(rules);
  const valid = slabs.length > 0 && errors.size === 0;

  const save = useMutation({
    mutationFn: () => saveAuctionRules(tournament.id, eventId, draft),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: rulesKey(tournament.id) }),
  });

  const setSlab = (index: number, patch: Partial<SlabDraft>) =>
    setSlabs((all) => all.map((slab, i) => (i === index ? { ...slab, ...patch } : slab)));

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && changed) save.mutate();
      }}
    >
      <fieldset className="mb-6">
        <legend className="field-label">Bid increments</legend>
        <p className="mb-3 text-sm text-ink-muted">
          From each starting price on, a bid raises the price by the step.
        </p>
        <ol>
          {slabs.map((slab, index) => {
            const error = errors.get(index);
            const errorId = `${id}-slab-${index}`;
            return (
              <li key={index} className="mb-3">
                <div className="flex items-end gap-2">
                  <label className="flex-1">
                    <span className="micro mb-1 block">From ({tournament.auctionCurrency})</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={slab.from}
                      onChange={(event) => setSlab(index, { from: event.target.value })}
                      aria-label={`Starting price of slab ${index + 1}`}
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? errorId : undefined}
                      style={rowControl}
                    />
                  </label>
                  <label className="flex-1">
                    <span className="micro mb-1 block">Step + ({tournament.auctionCurrency})</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={slab.step}
                      onChange={(event) => setSlab(index, { step: event.target.value })}
                      aria-label={`Step of slab ${index + 1}`}
                      aria-invalid={error ? true : undefined}
                      aria-describedby={error ? errorId : undefined}
                      style={rowControl}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setSlabs((all) => all.filter((_, i) => i !== index))}
                    disabled={slabs.length === 1}
                    aria-label={`Remove slab ${index + 1}`}
                    className="shrink-0 text-sm underline disabled:opacity-40"
                    style={{ minHeight: 44, minWidth: 44 }}
                  >
                    Remove
                  </button>
                </div>
                {error ? (
                  <p
                    id={errorId}
                    className="mt-1 text-sm"
                    style={{ color: "var(--color-attention)" }}
                  >
                    {error}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ol>
        <Button
          type="button"
          tone="quiet"
          onClick={() =>
            setSlabs((all) => {
              const last = all.at(-1);
              const from = last ? toNumber(last.from) : Number.NaN;
              return [
                ...all,
                {
                  from: Number.isInteger(from) ? String(from + 50) : "",
                  step: last?.step ?? "5",
                },
              ];
            })
          }
        >
          Add a slab
        </Button>
      </fieldset>

      <fieldset className="mb-6">
        <legend className="field-label">Purse reserve</legend>
        <p className="mb-3 text-sm text-ink-muted">
          A squad keeps enough to pay the base price for every player it still has to take.
        </p>
        <Segmented
          label="Purse reserve"
          value={purseReserve ? "on" : "off"}
          onChange={(value) => setPurseReserve(value === "on")}
          options={[
            { value: "on", label: "On" },
            { value: "off", label: "Off" },
          ]}
        />
      </fieldset>

      <Choice
        legend="Leftovers of an uneven lot"
        name={`${id}-leftovers`}
        value={leftovers}
        labels={LEFTOVERS_LABELS}
        onChange={setLeftovers}
      />
      <Choice
        legend="Still unsold when the lot closes"
        hint="An unsold player is offered again at the end of their lot, until sold or the auctioneer moves to the next lot."
        name={`${id}-unsold`}
        value={unsold}
        labels={UNSOLD_LABELS}
        onChange={setUnsold}
      />
      <Choice
        legend="Who comes up next"
        name={`${id}-next`}
        value={nextUp}
        labels={NEXT_UP_LABELS}
        onChange={setNextUp}
      />
      <Choice
        legend="Price of a player only one team can take"
        name={`${id}-forced`}
        value={forcedPrice}
        labels={FORCED_PRICE_LABELS}
        onChange={setForcedPrice}
      />

      <fieldset className="mb-6">
        <legend className="field-label">On the auction board</legend>
        <p className="mb-2 text-sm text-ink-muted">
          What the board shows about the player up, beside their photo and name.
        </p>
        {(
          [
            ["rating", "Rating"],
            ["level", "Level they gave themselves"],
            ["handSide", "Hand and court side"],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex min-h-[44px] items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={board[key]}
              onChange={(event) => setBoard((all) => ({ ...all, [key]: event.target.checked }))}
              className="h-5 w-5 shrink-0"
              style={{ accentColor: "var(--color-line)" }}
            />
            {label}
          </label>
        ))}
      </fieldset>

      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
      <Button type="submit" tone="primary" disabled={!valid || !changed || save.isPending}>
        {save.isPending ? "Saving…" : "Save rules"}
      </Button>
    </form>
  );
}

/** Pick one of a few policies, each a sentence: radios, not a dropdown, so all are read. */
function Choice<T extends string>({
  legend,
  hint,
  name,
  value,
  labels,
  onChange,
}: {
  legend: string;
  hint?: string;
  name: string;
  value: T;
  labels: Record<T, string>;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="mb-6">
      <legend className="field-label">{legend}</legend>
      {hint ? <p className="mb-1 text-sm text-ink-muted">{hint}</p> : null}
      {(Object.keys(labels) as T[]).map((option) => (
        <label key={option} className="flex min-h-[44px] items-center gap-3 text-sm">
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option}
            onChange={() => onChange(option)}
            className="h-5 w-5 shrink-0"
            style={{ accentColor: "var(--color-line)" }}
          />
          {labels[option]}
        </label>
      ))}
    </fieldset>
  );
}

/** One set of rules, read only. */
function RulesSummary({ rules, currency }: { rules: AuctionRules; currency: AuctionCurrency }) {
  const rows: [string, string][] = [
    ["Bid increments", slabsLine(rules.slabs, currency)],
    ["Purse reserve", rules.purseReserve ? "On" : "Off"],
    ["Leftovers of an uneven lot", LEFTOVERS_LABELS[rules.leftovers]],
    ["Still unsold when the lot closes", UNSOLD_LABELS[rules.unsold]],
    ["Who comes up next", NEXT_UP_LABELS[rules.nextUp]],
    ["Price of a player only one team can take", FORCED_PRICE_LABELS[rules.forcedPrice]],
    [
      "On the board",
      [
        rules.board.rating ? "Rating" : null,
        rules.board.level ? "Level" : null,
        rules.board.handSide ? "Hand and side" : null,
      ]
        .filter(Boolean)
        .join(", ") || "Name and photo only",
    ],
  ];
  return (
    <dl>
      {rows.map(([term, detail]) => (
        <div
          key={term}
          className="flex flex-col gap-0.5 border-b py-2 sm:flex-row sm:gap-4"
          style={{ borderColor: "var(--color-line-muted)" }}
        >
          <dt className="micro sm:w-48 sm:shrink-0 sm:pt-0.5">{term}</dt>
          <dd className="text-sm">{detail}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The way in from the Organise page: one row, like Lots. */
export function RulesRow({ tournament }: { tournament: Tournament }) {
  return <RulesLinkRow tournament={tournament} title="Bid increments and rules" />;
}

/**
 * The tournament page's Auction section, for organisers and captains only: the rules, then a row
 * for each pool's published lots, which shows only to those the database hands the lots to.
 */
export function AuctionLinks({ tournament }: { tournament: Tournament }) {
  // Lots reach only organisers and a pool's captains too, so nobody else would find anything
  // under the heading: the whole section is theirs.
  const viewer = useSeesRules(tournament.auction ? tournament.id : "");
  if (!tournament.auction || !viewer.sees) return null;
  return (
    <section className="mt-6 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-1">Auction</h2>
      <RulesLinkRow tournament={tournament} title="Auction rules" />
      {tournament.events.map((event) => (
        <LotsLinkRow key={event.id} tournament={tournament} event={event} />
      ))}
    </section>
  );
}

function RulesLinkRow({ tournament, title }: { tournament: Tournament; title: string }) {
  const { data } = useQuery({
    queryKey: rulesKey(tournament.id),
    queryFn: () => fetchAuctionRules(tournament.id),
  });
  const own = data ? data.pools.size : 0;
  const facts = data
    ? `${slabsLine(data.tournament.slabs, tournament.auctionCurrency)}${own > 0 ? ` · ${own} ${own === 1 ? "pool has its" : "pools have their"} own` : ""}`
    : " ";

  return (
    <ul>
      <li className="border-b" style={{ borderColor: "var(--color-line-muted)" }}>
        <Link
          to={`/tournaments/${tournament.id}/rules`}
          className="flex min-h-[56px] items-center gap-3 py-2"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-base font-bold">{title}</span>
            <span className="font-mono text-[11px] text-ink-muted">{facts}</span>
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            aria-hidden
            className="shrink-0"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </li>
    </ul>
  );
}
